import React, { useEffect, useRef, useState } from "react";
import { Accessibility, X, RotateCcw } from "lucide-react";

const KEY = "a11y-prefs";
const DEFAULTS = { text: 0, contrast: false, links: false, motion: false, font: false };
const TEXT_STEPS = ["Normale", "Grande", "Molto grande"];
const OPTIONS = [
  { key: "contrast", label: "Contrasto elevato" },
  { key: "links", label: "Sottolinea i link" },
  { key: "font", label: "Carattere più leggibile" },
  { key: "motion", label: "Riduci le animazioni" },
];

function load() {
  try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || "{}") }; } catch { return DEFAULTS; }
}

function apply(p) {
  const c = document.documentElement.classList;
  c.toggle("a11y-text-1", p.text === 1);
  c.toggle("a11y-text-2", p.text === 2);
  OPTIONS.forEach((o) => c.toggle(`a11y-${o.key}`, !!p[o.key]));
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={onChange} data-testid={testId}
      className="w-full flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm text-left hover:bg-lilla/60 dark:hover:bg-cream/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-fucsia">
      <span>{label}</span>
      <span aria-hidden className={`relative h-5 w-9 rounded-full transition-colors ${checked ? "bg-fucsia" : "bg-viola/20 dark:bg-cream/25"}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
      </span>
    </button>
  );
}

export default function AccessibilityMenu() {
  const [prefs, setPrefs] = useState(load);
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  useEffect(() => { apply(prefs); localStorage.setItem(KEY, JSON.stringify(prefs)); }, [prefs]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); btnRef.current?.focus(); } };
    const onClick = (e) => { if (!e.target.closest(".a11y-widget")) setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, [open]);

  return (
    <div className="a11y-widget fixed left-0 top-1/2 -translate-y-1/2 z-[60] print:hidden">
      {open && (
        <div id="a11y-panel" role="dialog" aria-label="Opzioni di accessibilità" data-testid="a11y-panel"
          className="absolute left-9 top-1/2 -translate-y-1/2 max-h-[85vh] overflow-y-auto w-72 max-w-[calc(100vw-3rem)] rounded-2xl border border-viola/15 bg-cream p-4 text-viola shadow-2xl dark:bg-viola dark:text-cream dark:border-cream/15">
          <div className="flex items-center justify-between">
            <p className="font-semibold">Accessibilità</p>
            <button type="button" onClick={() => setOpen(false)} aria-label="Chiudi opzioni di accessibilità" className="grid h-8 w-8 place-items-center rounded-full hover:bg-lilla/60 dark:hover:bg-cream/10" data-testid="a11y-close">
              <X size={16} aria-hidden />
            </button>
          </div>
          <p className="mt-3 text-xs font-semibold uppercase tracking-widest opacity-70" id="a11y-text-label">Dimensione del testo</p>
          <div className="mt-2 grid grid-cols-3 gap-1" role="radiogroup" aria-labelledby="a11y-text-label">
            {TEXT_STEPS.map((label, i) => (
              <button key={label} type="button" role="radio" aria-checked={prefs.text === i} onClick={() => setPrefs((p) => ({ ...p, text: i }))} data-testid={`a11y-text-${i}`}
                className={`rounded-lg px-2 py-2 text-xs font-semibold border ${prefs.text === i ? "bg-viola text-cream border-viola dark:bg-fucsia dark:border-fucsia" : "border-viola/20 dark:border-cream/20"}`}>
                <span aria-hidden className="block" style={{ fontSize: `${14 + i * 3}px` }}>A</span>{label}
              </button>
            ))}
          </div>
          <div className="mt-3 space-y-1">
            {OPTIONS.map((o) => (
              <Toggle key={o.key} label={o.label} checked={!!prefs[o.key]} onChange={() => setPrefs((p) => ({ ...p, [o.key]: !p[o.key] }))} testId={`a11y-${o.key}`} />
            ))}
          </div>
          <button type="button" onClick={() => setPrefs(DEFAULTS)} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-fucsia hover:underline" data-testid="a11y-reset">
            <RotateCcw size={12} aria-hidden /> Ripristina
          </button>
        </div>
      )}
      <button ref={btnRef} type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls="a11y-panel" aria-label="Opzioni di accessibilità" data-testid="a11y-toggle"
        className="grid h-12 w-6 sm:w-7 place-items-center rounded-r-xl bg-viola text-cream shadow-lg hover:bg-fucsia transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-fucsia dark:bg-giallo dark:text-viola">
        <Accessibility size={18} aria-hidden />
      </button>
    </div>
  );
}
